import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { processArtworkImage, dataUrlSizeKB } from "@/lib/imageProcessor";
import { toast } from "sonner";

const EMPTY = {
  title: "",
  description: "",
  collection_slug: "",
  medium: "Oil",
  support: "Canvas",
  orientation: "landscape",
  year: new Date().getFullYear(),
  image_url: "",
  price_a4: 80,
  price_a3: 140,
  featured: false,
  published: true,
};

export default function AdminArtworkEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(!!id);
  const [pending, setPending] = useState(false);
  const fileRef = useRef(null);
  const [applyWatermark, setApplyWatermark] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    api.get("/collections").then((r) => setCollections(r.data));
    if (id) {
      api.get("/admin/artworks").then((r) => {
        const found = r.data.find((a) => a.id === id);
        if (found) setForm(found);
        setLoading(false);
      });
    }
  }, [id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      toast.error("Original file must be under 15MB.");
      return;
    }
    setProcessing(true);
    try {
      const dataUrl = await processArtworkImage(f, {
        watermarkText: applyWatermark ? "ART BY SUSAN DENNY" : "",
      });
      update("image_url", dataUrl);
      const kb = dataUrlSizeKB(dataUrl);
      toast.success(
        applyWatermark
          ? `Image resized to 1600px and watermarked (${kb} KB).`
          : `Image resized to 1600px (${kb} KB).`,
      );
    } catch (err) {
      toast.error(err.message || "Could not process the image.");
    } finally {
      setProcessing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setPending(true);
    try {
      const payload = {
        ...form,
        year: form.year ? Number(form.year) : null,
        price_a4: Number(form.price_a4),
        price_a3: Number(form.price_a3),
      };
      if (id) {
        await api.put(`/admin/artworks/${id}`, payload);
      } else {
        await api.post("/admin/artworks", payload);
      }
      toast.success("Saved");
      navigate("/admin/artworks");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPending(false);
    }
  };

  if (loading) return <p className="eyebrow text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="admin-artwork-edit">
      <Link to="/admin/artworks" className="eyebrow text-muted-foreground mb-6 inline-block">← All artworks</Link>
      <h1 className="font-serif text-5xl font-light mb-10">{id ? "Edit artwork" : "New artwork"}</h1>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12">
        <div className="space-y-6">
          <Field label="Title" v={form.title} onChange={(v) => update("title", v)} required testid="artwork-title" />

          <div>
            <label className="eyebrow mb-2 block">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              rows={5}
              data-testid="artwork-description"
              className="w-full bg-transparent border border-border focus:border-foreground p-3 outline-none resize-y"
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="eyebrow mb-2 block">Collection</label>
              <select
                value={form.collection_slug}
                onChange={(e) => update("collection_slug", e.target.value)}
                data-testid="artwork-collection"
                className="w-full bg-transparent border-b border-border py-3 outline-none"
              >
                <option value="">(none)</option>
                {collections.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="eyebrow mb-2 block">Orientation</label>
              <select
                value={form.orientation}
                onChange={(e) => update("orientation", e.target.value)}
                data-testid="artwork-orientation"
                className="w-full bg-transparent border-b border-border py-3 outline-none"
              >
                <option value="landscape">Landscape</option>
                <option value="portrait">Portrait</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <Field label="Medium" v={form.medium} onChange={(v) => update("medium", v)} testid="artwork-medium" />
            <Field label="Support" v={form.support} onChange={(v) => update("support", v)} testid="artwork-support" />
          </div>

          <div className="grid grid-cols-3 gap-6">
            <Field label="Year" type="number" v={form.year || ""} onChange={(v) => update("year", v)} testid="artwork-year" />
            <Field label="A4 price (AUD)" type="number" v={form.price_a4} onChange={(v) => update("price_a4", v)} testid="artwork-price-a4" />
            <Field label="A3 price (AUD)" type="number" v={form.price_a3} onChange={(v) => update("price_a3", v)} testid="artwork-price-a3" />
          </div>

          <div className="flex items-center gap-8 pt-4">
            <Toggle label="Published" checked={form.published} onChange={(v) => update("published", v)} testid="artwork-published" />
            <Toggle label="Featured" checked={form.featured} onChange={(v) => update("featured", v)} testid="artwork-featured" />
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="eyebrow mb-2 block">Image</label>
            <div className="aspect-[4/5] bg-secondary mb-3 relative">
              {form.image_url
                ? <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><span className="eyebrow">No image</span></div>}
              {processing && (
                <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                  <span className="eyebrow text-foreground">Processing image…</span>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="artwork-image-file" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={processing}
              className="btn-outline-ink w-full disabled:opacity-50"
            >
              {processing ? "Processing…" : form.image_url ? "Replace image" : "Upload image"}
            </button>

            <label className="flex items-start gap-3 mt-4 cursor-pointer" data-testid="artwork-watermark-toggle">
              <input
                type="checkbox"
                checked={applyWatermark}
                onChange={(e) => setApplyWatermark(e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm">
                <span className="block">Apply watermark</span>
                <span className="text-xs text-muted-foreground block leading-relaxed">
                  Resizes to 1600 px on the long edge and adds a tiled "ART BY SUSAN DENNY" mark at 30% opacity.
                </span>
              </span>
            </label>

            <p className="eyebrow text-muted-foreground mt-6">Or paste an image URL:</p>
            <input
              type="text"
              value={form.image_url?.startsWith("data:") ? "" : form.image_url}
              onChange={(e) => update("image_url", e.target.value)}
              placeholder="https://…"
              data-testid="artwork-image-url"
              className="w-full bg-transparent border-b border-border py-2 outline-none text-sm mt-2"
            />
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              External URLs are not resized or watermarked — best for already-prepared images.
            </p>
          </div>

          <button type="submit" disabled={pending} className="btn-ink w-full" data-testid="artwork-save">
            {pending ? "Saving…" : id ? "Save changes" : "Publish artwork"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, v, onChange, required, type = "text", testid }) {
  return (
    <div>
      <label className="eyebrow mb-2 block">{label}{required && " *"}</label>
      <input
        type={type}
        required={required}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border-b border-border focus:border-foreground py-3 outline-none"
      />
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <span className="eyebrow">{label}</span>
    </label>
  );
}
